import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, FileText, CheckCircle2, XCircle, Clock, ChevronRight,
  ArrowLeft, Download, ZoomIn, Mail, Phone, MapPin, Calendar,
  AlertTriangle, Eye
} from "lucide-react";
import { Button } from "components/ui/button";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { Badge } from "components/ui/badge";

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

type DocStatus = "pending" | "approved" | "rejected";

interface Document {
  type: string;
  fileName: string;
  uploadDate: string;
  status: DocStatus;
  notes?: string;
}

interface VerificationRequest {
  id: string;
  providerName: string;
  ownerName: string;
  email: string;
  phone: string;
  location: string;
  category: string;
  submittedDate: string;
  overallStatus: DocStatus;
  avatar: string;
  documents: Document[];
}

const mockRequests: VerificationRequest[] = [
  {
    id: "V-001",
    providerName: "Ink & Soul Tattoos",
    ownerName: "Jade Rivera",
    email: "jade@inksoul.com",
    phone: "+1 555-0303",
    location: "Austin, TX",
    category: "Tattoo",
    submittedDate: "Mar 25, 2026",
    overallStatus: "pending",
    avatar: "JR",
    documents: [
      { type: "Government ID", fileName: "jade_id_front.jpg", uploadDate: "Mar 25, 2026", status: "pending" },
      { type: "Business License", fileName: "ink_soul_license.pdf", uploadDate: "Mar 25, 2026", status: "pending" },
      { type: "Health & Safety Certificate", fileName: "health_cert.pdf", uploadDate: "Mar 25, 2026", status: "pending" },
      { type: "Portfolio / Certification", fileName: "tattoo_certs.pdf", uploadDate: "Mar 25, 2026", status: "pending" },
    ],
  },
  {
    id: "V-002",
    providerName: "Bella Lash Studio",
    ownerName: "Mia Thompson",
    email: "mia@bellalash.com",
    phone: "+1 555-0606",
    location: "San Diego, CA",
    category: "Beauty",
    submittedDate: "Mar 22, 2026",
    overallStatus: "pending",
    avatar: "MT",
    documents: [
      { type: "Government ID", fileName: "mia_passport.jpg", uploadDate: "Mar 22, 2026", status: "approved" },
      { type: "Business License", fileName: "bella_lash_license.pdf", uploadDate: "Mar 22, 2026", status: "pending" },
      { type: "Cosmetology License", fileName: "cosmo_license.pdf", uploadDate: "Mar 22, 2026", status: "pending" },
    ],
  },
  {
    id: "V-003",
    providerName: "Serene Touch Wellness",
    ownerName: "David Okafor",
    email: "david@serenetouch.com",
    phone: "+1 555-0707",
    location: "Atlanta, GA",
    category: "Wellness",
    submittedDate: "Mar 18, 2026",
    overallStatus: "approved",
    avatar: "DO",
    documents: [
      { type: "Government ID", fileName: "david_id.jpg", uploadDate: "Mar 18, 2026", status: "approved" },
      { type: "Business License", fileName: "serene_license.pdf", uploadDate: "Mar 18, 2026", status: "approved" },
      { type: "Massage Therapy License", fileName: "massage_cert.pdf", uploadDate: "Mar 18, 2026", status: "approved" },
    ],
  },
  {
    id: "V-004",
    providerName: "Clip & Style Barber",
    ownerName: "Tony Garcia",
    email: "tony@clipstyle.com",
    phone: "+1 555-0808",
    location: "Houston, TX",
    category: "Barber",
    submittedDate: "Mar 15, 2026",
    overallStatus: "rejected",
    avatar: "TG",
    documents: [
      { type: "Government ID", fileName: "tony_id.jpg", uploadDate: "Mar 15, 2026", status: "approved" },
      { type: "Business License", fileName: "clip_license.pdf", uploadDate: "Mar 15, 2026", status: "rejected", notes: "Document expired. Please upload a current license." },
      { type: "Barber License", fileName: "barber_cert.jpg", uploadDate: "Mar 15, 2026", status: "rejected", notes: "Image is too blurry to verify. Please re-upload." },
    ],
  },
];

const docStatusConfig: Record<DocStatus, { icon: typeof CheckCircle2; className: string; label: string }> = {
  pending: { icon: Clock, className: "text-amber-600 bg-amber-50 border-amber-200", label: "Pending" },
  approved: { icon: CheckCircle2, className: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "Approved" },
  rejected: { icon: XCircle, className: "text-red-600 bg-red-50 border-red-200", label: "Rejected" },
};

const Verification = () => {
  const [filter, setFilter] = useState<"all" | DocStatus>("all");
  const [selectedRequest, setSelectedRequest] = useState<VerificationRequest | null>(null);
  const [requests, setRequests] = useState(mockRequests);
  const [rejectingDoc, setRejectingDoc] = useState<number | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  const filtered = filter === "all" ? requests : requests.filter((r) => r.overallStatus === filter);

  const pendingCount = requests.filter((r) => r.overallStatus === "pending").length;

  const updateDocStatus = (reqId: string, docIndex: number, status: DocStatus, notes?: string) => {
    setRequests((prev) =>
      prev.map((r) => {
        if (r.id !== reqId) return r;
        const docs = [...r.documents];
        docs[docIndex] = { ...docs[docIndex], status, notes: notes || docs[docIndex].notes };
        const allApproved = docs.every((d) => d.status === "approved");
        const anyRejected = docs.some((d) => d.status === "rejected");
        const overallStatus: DocStatus = allApproved ? "approved" : anyRejected ? "rejected" : "pending";
        const updated = { ...r, documents: docs, overallStatus };
        if (selectedRequest?.id === reqId) setSelectedRequest(updated);
        return updated;
      })
    );
    setRejectingDoc(null);
    setRejectNote("");
  };

  return (
    <motion.div key="verification" {...fadeIn}>
      <AnimatePresence mode="wait">
        {selectedRequest ? (
          <motion.div key="detail" {...fadeIn}>
            {/* Detail header */}
            <button
              onClick={() => setSelectedRequest(null)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Verification Queue
            </button>

            <div className="rounded-xl border border-border bg-background p-4 mb-4">
              <div className="flex items-start gap-3">
                <Avatar className="h-14 w-14">
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">{selectedRequest.avatar}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{selectedRequest.providerName}</h3>
                      <p className="text-sm text-muted-foreground">{selectedRequest.ownerName}</p>
                    </div>
                    {(() => {
                      const cfg = docStatusConfig[selectedRequest.overallStatus];
                      const Icon = cfg.icon;
                      return (
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${cfg.className}`}>
                          <Icon className="h-3 w-3" /> {cfg.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{selectedRequest.email}</span>
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{selectedRequest.phone}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{selectedRequest.location}</span>
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Submitted {selectedRequest.submittedDate}</span>
                  </div>
                  <Badge variant="secondary" className="mt-2 text-[10px]">{selectedRequest.category}</Badge>
                </div>
              </div>
            </div>

            {/* Documents */}
            <h4 className="font-semibold text-foreground mb-3">Submitted Documents</h4>
            <div className="space-y-3">
              {selectedRequest.documents.map((doc, i) => {
                const cfg = docStatusConfig[doc.status];
                const StatusIcon = cfg.icon;
                return (
                  <div key={i} className="rounded-xl border border-border bg-background p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-lg bg-muted/50">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{doc.type}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{doc.fileName}</p>
                          <p className="text-[11px] text-muted-foreground">Uploaded {doc.uploadDate}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cfg.className}`}>
                        <StatusIcon className="h-3 w-3" /> {cfg.label}
                      </span>
                    </div>

                    {doc.notes && (
                      <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 p-2.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-red-700">{doc.notes}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-3 flex items-center gap-2">
                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
                        <Eye className="h-3 w-3" /> Preview
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
                        <Download className="h-3 w-3" /> Download
                      </Button>

                      {doc.status === "pending" && (
                        <>
                          <div className="flex-1" />
                          <Button
                            size="sm"
                            className="h-8 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => updateDocStatus(selectedRequest.id, i, "approved")}
                          >
                            <CheckCircle2 className="h-3 w-3" /> Approve
                          </Button>
                          {rejectingDoc === i ? (
                            <div className="flex items-center gap-2">
                              <input
                                autoFocus
                                value={rejectNote}
                                onChange={(e) => setRejectNote(e.target.value)}
                                placeholder="Reason for rejection..."
                                className="h-8 rounded-md border border-border bg-background px-2 text-xs w-44 focus:outline-none focus:ring-1 focus:ring-ring"
                              />
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-8 text-xs"
                                onClick={() => updateDocStatus(selectedRequest.id, i, "rejected", rejectNote)}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 text-xs"
                                onClick={() => { setRejectingDoc(null); setRejectNote(""); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="h-8 text-xs gap-1.5"
                              onClick={() => setRejectingDoc(i)}
                            >
                              <XCircle className="h-3 w-3" /> Reject
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bulk actions */}
            {selectedRequest.documents.some((d) => d.status === "pending") && (
              <div className="flex items-center gap-3 mt-4 p-4 rounded-xl border border-border bg-muted/30">
                <p className="text-sm text-muted-foreground flex-1">
                  {selectedRequest.documents.filter((d) => d.status === "pending").length} document(s) still pending review
                </p>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                  onClick={() => {
                    selectedRequest.documents.forEach((d, i) => {
                      if (d.status === "pending") updateDocStatus(selectedRequest.id, i, "approved");
                    });
                  }}
                >
                  Approve All Pending
                </Button>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div key="list" {...fadeIn}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Verification Queue</h3>
                {pendingCount > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">{pendingCount} request(s) awaiting review</p>
                )}
              </div>
              <div className="flex rounded-lg border border-border overflow-hidden">
                {(["all", "pending", "approved", "rejected"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                      filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary counters */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {(["pending", "approved", "rejected"] as DocStatus[]).map((s) => {
                const cfg = docStatusConfig[s];
                const Icon = cfg.icon;
                const count = requests.filter((r) => r.overallStatus === s).length;
                return (
                  <button
                    key={s}
                    onClick={() => setFilter(s)}
                    className={`rounded-xl border p-3.5 text-left transition-colors ${
                      filter === s ? "border-primary bg-primary/5" : "border-border bg-background hover:bg-muted/30"
                    }`}
                  >
                    <Icon className={`h-5 w-5 mb-2 ${cfg.className.split(" ")[0]}`} />
                    <p className="text-xl font-bold text-foreground">{count}</p>
                    <p className="text-[11px] text-muted-foreground capitalize">{s}</p>
                  </button>
                );
              })}
            </div>

            {/* Request list */}
            <div className="space-y-3">
              {filtered.length === 0 && (
                <div className="py-12 text-center">
                  <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No {filter === "all" ? "" : filter} verification requests</p>
                </div>
              )}
              {filtered.map((req) => {
                const cfg = docStatusConfig[req.overallStatus];
                const StatusIcon = cfg.icon;
                const pendingDocs = req.documents.filter((d) => d.status === "pending").length;
                return (
                  <button
                    key={req.id}
                    onClick={() => setSelectedRequest(req)}
                    className="w-full text-left rounded-xl border border-border bg-background p-4 hover:bg-muted/20 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-muted text-muted-foreground text-sm font-medium">{req.avatar}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-foreground">{req.providerName}</h4>
                            <p className="text-xs text-muted-foreground">{req.ownerName} · {req.category}</p>
                          </div>
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cfg.className}`}>
                            <StatusIcon className="h-3 w-3" /> {cfg.label}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{req.location}</span>
                            <span>{req.documents.length} docs</span>
                            {pendingDocs > 0 && (
                              <span className="text-amber-600 font-medium">{pendingDocs} pending</span>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Verification;
